import React, { useEffect, useState } from 'react';
import { todayISO, moneyTight, localeDate } from '../../utils/region';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import StaffFileLibrary from '../FileLibrary/StaffFileLibrary';
import { brandOr } from '../../utils/brand';

const fmtDate = (ymd: string) => { const p = (ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (ymd || '—'); };
const monthLabel = (ym: string) => { const [y, m] = (ym || '').split('-'); return m ? new Date(Number(y), Number(m) - 1, 1).toLocaleString(localeDate(), { month: 'short' }) + ' ' + y : ym; };

// Western zodiac from YYYY-MM-DD.
function zodiac(b: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(b || ''); if (!m) return '';
  const mo = +m[1], d = +m[2];
  const last = [19, 18, 20, 19, 20, 20, 22, 22, 21, 22, 21, 21];
  const signs = ['Capricorn', 'Aquarius', 'Pisces', 'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn'];
  return d > last[mo - 1] ? signs[mo] : signs[mo - 1];
}

type Period = 'month' | 'year' | 'all';
function periodRange(p: Period): { from: string; to: string } {
  const now = new Date(todayISO());
  const y = now.getFullYear(), m = now.getMonth();
  if (p === 'all') return { from: '', to: '' };
  if (p === 'year') return { from: `${y}-01-01`, to: `${y}-12-31` };
  const first = `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const lastD = new Date(y, m + 1, 0).getDate();
  return { from: first, to: `${y}-${String(m + 1).padStart(2, '0')}-${String(lastD).padStart(2, '0')}` };
}

const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 };
const cardTitle: React.CSSProperties = { fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 };
const kvLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.03em' };
const stat: React.CSSProperties = { fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' };

export function StaffDashboardView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [services, setServices] = useState<any[]>([]);
  const [docs, setDocs] = useState<any[]>([]);
  const [stats, setStats] = useState<{ rows: any[]; totals: { active: number; closed: number } }>({ rows: [], totals: { active: 0, closed: 0 } });
  const [fin, setFin] = useState<any>({ clientsServed: 0, servicesServed: 0, invoiced: 0, quoted: 0, receipted: 0 });
  const [period, setPeriod] = useState<Period>('month');
  const [tab, setTab] = useState<'overview' | 'jobs' | 'services' | 'docs' | 'files'>('overview');

  useEffect(() => {
    api.staffProfile().then(setProfile).catch(() => {});
    api.staffServices().then((d: any) => setServices(d.services || [])).catch(() => {});
    api.staffDocuments().then((d: any) => setDocs(d.documents || [])).catch(() => {});
    api.jobsStats('mine').then((d: any) => setStats({ rows: d.rows || [], totals: d.totals || { active: 0, closed: 0 } })).catch(() => {});
  }, []);
  useEffect(() => { const r = periodRange(period); api.staffFinancials(r.from, r.to).then(setFin).catch(() => {}); }, [period]);

  const periodBtn = (p: Period, label: string) => (
    <button key={p} onClick={() => setPeriod(p)} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid', cursor: 'pointer', borderColor: period === p ? 'var(--accent)' : 'var(--border-strong)', background: period === p ? 'var(--accent)' : 'transparent', color: period === p ? '#fff' : 'var(--text-secondary)' }}>{label}</button>
  );
  const tabBtn = (key: typeof tab, label: string) => (
    <button key={key} onClick={() => setTab(key)} style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, border: 'none', borderBottom: '2px solid', borderColor: tab === key ? 'var(--accent)' : 'transparent', background: 'transparent', color: tab === key ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer' }}>{label}</button>
  );
  const soc = (icon: string, val: string) => val ? <a href={/^https?:/.test(val) ? val : `https://${val}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}><i className={`ti ti-brand-${icon}`} style={{ fontSize: 18 }} /></a> : null;
  const kv = (label: string, value: React.ReactNode) => <div><div style={kvLabel}>{label}</div><div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginTop: 2 }}>{value || '—'}</div></div>;

  const byCompany: Record<string, any[]> = {};
  services.forEach(s => { (byCompany[s.company || '—'] = byCompany[s.company || '—'] || []).push(s); });

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 18, background: 'var(--bg-app)' }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{profile?.name || profile?.email || t('sd_title')}</div>
        <div style={{ flex: 1 }} />
        {tab === 'overview' && <>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('sd_period')}:</span>
          {periodBtn('month', t('sd_this_month'))}{periodBtn('year', t('sd_this_year'))}{periodBtn('all', t('sd_all'))}
        </>}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginBottom: 16, flexWrap: 'wrap' }}>
        {tabBtn('overview', t('sd_tab_overview'))}
        {tabBtn('jobs', t('sd_jobs'))}
        {tabBtn('services', t('sd_my_services'))}
        {tabBtn('docs', t('sd_docs_in_hand'))}
        {tabBtn('files', t('sd_files'))}
      </div>

      {tab === 'overview' && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 14 }}>
        {/* Basic info */}
        <div style={card}>
          <div style={cardTitle}><i className="ti ti-user" />{t('sd_basic')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {kv(t('sd_name'), profile?.name)}
            {kv(t('sd_gender'), profile?.gender)}
            {kv(t('sd_birthday'), profile?.birthday ? fmtDate(profile.birthday) : '')}
            {kv(t('sd_zodiac'), zodiac(profile?.birthday || ''))}
            {kv(t('sd_hire_date'), profile?.hire_date ? fmtDate(profile.hire_date) : '')}
            {kv('Email', profile?.email)}
          </div>
          {(profile?.linkedin || profile?.facebook || profile?.instagram) && (
            <div style={{ display: 'flex', gap: 14, marginTop: 2 }}>{soc('linkedin', profile?.linkedin)}{soc('facebook', profile?.facebook)}{soc('instagram', profile?.instagram)}</div>
          )}
        </div>

        {/* Financials */}
        <div style={card}>
          <div style={cardTitle}><i className="ti ti-chart-bar" />{t('sd_financials')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><div style={kvLabel}>{t('sd_clients_served')}</div><div style={stat}>{fin.clientsServed}</div></div>
            <div><div style={kvLabel}>{t('sd_services_served')}</div><div style={stat}>{fin.servicesServed}</div></div>
          </div>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span style={{ color: 'var(--text-secondary)' }}>{t('sd_invoiced')}</span><strong>{moneyTight(fin.invoiced)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span style={{ color: 'var(--text-secondary)' }}>{t('sd_quoted')}</span><strong>{moneyTight(fin.quoted)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span style={{ color: 'var(--text-secondary)' }}>{t('sd_receipted')}</span><strong style={{ color: '#15803D' }}>{moneyTight(fin.receipted)}</strong></div>
          </div>
        </div>
      </div>
      )}

      {tab === 'jobs' && (
        <div style={card}>
          <div style={cardTitle}><i className="ti ti-briefcase" />{t('sd_jobs')}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => navigate('/m/active_jobs')} style={{ flex: 1, textAlign: 'left', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--bg-app)', cursor: 'pointer' }}><div style={kvLabel}>{t('sd_active')}</div><div style={{ ...stat, color: 'var(--accent)' }}>{stats.totals.active}</div></button>
            <button onClick={() => navigate('/m/closed_jobs')} style={{ flex: 1, textAlign: 'left', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--bg-app)', cursor: 'pointer' }}><div style={kvLabel}>{t('sd_closed')}</div><div style={stat}>{stats.totals.closed}</div></button>
          </div>
          <div style={{ maxHeight: 160, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              {/* The headings in Active Jobs' colour and weight, over its line, at its padding (Calem, report 4bd4ae4e, 2nd
                  opening: "Make all menus in the SLF system look like this: the same height, text color, no bold,
                  horizontal line colors etc."): black at the regular weight, where they were light grey at semibold
                  (600), 10px above and below, where Month had 4 and the others none, over the darker --border-strong
                  line. They keep the rows' 12.5px, having no size of their own, and no padding at the sides, as the rows. */}
              <thead><tr><th style={{ textAlign: 'left', color: 'var(--text-primary)', fontWeight: 400, padding: '10px 0', borderBottom: '1px solid var(--border-strong)' }}>{t('sd_month')}</th><th style={{ textAlign: 'right', color: 'var(--text-primary)', fontWeight: 400, padding: '10px 0', borderBottom: '1px solid var(--border-strong)' }}>{t('sd_active')}</th><th style={{ textAlign: 'right', color: 'var(--text-primary)', fontWeight: 400, padding: '10px 0', borderBottom: '1px solid var(--border-strong)' }}>{t('sd_closed')}</th></tr></thead>
              <tbody>{stats.rows.map((r: any) => (
                <tr key={r.ym}><td style={{ padding: '4px 0' }}>{monthLabel(r.ym)}</td><td style={{ textAlign: 'right', color: 'var(--accent)', fontWeight: 600 }}>{r.active}</td><td style={{ textAlign: 'right' }}>{r.closed}</td></tr>
              ))}{!stats.rows.length && <tr><td colSpan={3} style={{ color: 'var(--text-tertiary)', padding: 8 }}>—</td></tr>}</tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'services' && (
        <div style={card}>
          <div style={cardTitle}><i className="ti ti-packages" />{t('sd_my_services')}</div>
          <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Object.entries(byCompany).map(([co, list]) => (
              <div key={co}>
                <div style={{ ...kvLabel, marginBottom: 4 }}>{co}</div>
                {list.map(s => <div key={s.id} style={{ fontSize: 13, padding: '3px 0', display: 'flex', gap: 8 }}><span style={{ fontWeight: 600 }}>{s.name}</span>{s.code && <span style={{ color: 'var(--text-tertiary)' }}>· {s.code}</span>}</div>)}
              </div>
            ))}
            {!services.length && <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{t('sd_none')}</div>}
          </div>
        </div>
      )}

      {tab === 'docs' && (
        <div style={card}>
          <div style={cardTitle}><i className="ti ti-files" />{t('sd_docs_in_hand')}</div>
          <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {docs.map((d: any) => {
              let labels: string[] = []; try { labels = JSON.parse(d.documents || '[]'); } catch { /* */ }
              return <div key={d.id} style={{ fontSize: 12.5, borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
                <div style={{ fontWeight: 600 }}>{`${d.client_first || ''} ${d.client_last || ''}`.trim() || '—'}</div>
                <div style={{ color: 'var(--text-secondary)' }}>{labels.join(', ') || '—'} · {d.count_total} ({d.count_originals} orig.)</div>
              </div>;
            })}
            {!docs.length && <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{t('sd_none')}</div>}
          </div>
        </div>
      )}

      {tab === 'files' && (
        <div style={card}>
          <StaffFileLibrary />
        </div>
      )}
    </div>
  );
}

export default function StaffDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_staff_dashboard')}</div>
      </header>
      <StaffDashboardView />
    </div>
  );
}
